<script setup lang="ts">
/**
 * H13 → H14 — Home Search drawer to spec
 * Figma RedisignHomePage `50:10142` desktop / `50:10480` mobile
 * States: 1 Idle · 2 Typing · 3 Photo + empty (Add detail) · 4 Photo + text
 * · 5 Empty (live, Or browse) · 6/7 Photo select (detected / nothing)
 * Live: preview via POST /api/v1/search · suggestions via autocomplete
 * · photo via uploadImage + get-image-parts.
 * ≠ Track S PLP
 */
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { getImageParts } from '@/api/images'
import { uploadImage } from '@/features/search/api/search'
import { useCategoryTreeQuery } from '@/queries/categories'
import type { UploadImageResponse } from '@/types/searchItemsTypes'
import { FairButton } from '@/ui/primitives/FairButton'
import { FairIcon } from '@/ui/primitives/FairIcon'
import { FairIconButton } from '@/ui/primitives/FairIconButton'
import { FairIconContainer } from '@/ui/primitives/FairIconContainer'
import { FairLink } from '@/ui/primitives/FairLink'
import {
  getFairHomeImageSearchPartFallbackKey,
  normalizeFairHomeImageSearchKey,
  parseFairHomeImageSearchParts,
  type FairHomeImageSearchPart,
} from '@/widgets/home/fair/fair-home-image-search.data'
import FairHomeV2SearchPhotoSelect from './FairHomeV2SearchPhotoSelect.vue'
import FairHomeV2SearchPreview from './FairHomeV2SearchPreview.vue'
import {
  buildFairHomeV2SearchBrowseLocation,
  buildFairHomeV2SearchLocation,
  FAIR_HOME_V2_SEARCH_ADD_DETAIL_CHIP_KEYS,
  FAIR_HOME_V2_SEARCH_DRAWER_WIDTH_DESKTOP,
  FAIR_HOME_V2_SEARCH_DRAWER_WIDTH_RESULTS,
  FAIR_HOME_V2_SEARCH_WHOLE_OUTFIT_ID,
  loadFairHomeV2SearchImageSize,
  mapFairHomeV2SearchPhotoParts,
  recentItemLabel,
  resolveFairHomeV2SearchBrowseCategories,
  resolveFairHomeV2SearchDrawerMode,
  splitFairHomeV2SearchSuggestionMatch,
  type FairHomeV2SearchBrowseCategory,
  type FairHomeV2SearchDrawerView,
  type FairHomeV2SearchPhotoPart,
  type FairHomeV2SearchPreviewCard,
  type FairHomeV2SearchRecentItem,
} from './fair-home-v2-search-drawer.data'
import { useFairHomeV2SearchLive } from './useFairHomeV2SearchLive'
import { useFairHomeV2SearchRecent } from './useFairHomeV2SearchRecent'
import './fair-home-v2-search-drawer.css'

const props = withDefaults(
  defineProps<{
    open?: boolean
    compact?: boolean
    /** Header gender tab (`women` / `men` / `kids`) — scopes preview + PLP. */
    audience?: string
  }>(),
  {
    open: false,
    compact: false,
    audience: 'women',
  },
)

const emit = defineEmits<{
  'update:open': [value: boolean]
}>()

const { t } = useI18n()
const router = useRouter()
const recentEnabled = computed(() => props.open)
const { items: recentItems, pushRecent, clearRecent } = useFairHomeV2SearchRecent({
  enabled: recentEnabled,
})

/* ------------------------------------------------------------------ */
/* Input / photo state                                                 */
/* ------------------------------------------------------------------ */

const query = ref('')
const view = ref<FairHomeV2SearchDrawerView>('search')

/** Local preview (blob) or server URL restored from Recent. */
const photoUrl = ref<string | null>(null)
/** Server image ref (upload `img_url`) — search context + PLP `?image=`. */
const photoKey = ref<string | null>(null)
const photoTag = ref<string | null>(null)
const photoParts = ref<FairHomeV2SearchPhotoPart[]>([])
const photoSelectedId = ref(FAIR_HOME_V2_SEARCH_WHOLE_OUTFIT_ID)
const photoDetecting = ref(false)
const photoObjectUrl = ref<string | null>(null)
let photoToken = 0

const inputRef = ref<HTMLTextAreaElement | null>(null)
const fileInputRef = ref<HTMLInputElement | null>(null)

/** Photo counts once a tag was chosen in photo-select (States 3–4). */
const hasPhoto = computed(() => Boolean(photoUrl.value && photoTag.value))

/* ------------------------------------------------------------------ */
/* Live preview + suggestions                                           */
/* ------------------------------------------------------------------ */

const liveImageId = computed(() => (hasPhoto.value ? photoKey.value : null))
const liveEnabled = computed(() => props.open && view.value === 'search')
const audienceRef = computed(() => props.audience)

const live = useFairHomeV2SearchLive({
  query,
  imageId: liveImageId,
  audience: audienceRef,
  enabled: liveEnabled,
})

const mode = computed(() =>
  resolveFairHomeV2SearchDrawerMode({
    query: query.value,
    resultCount: live.total.value,
    hasPhoto: hasPhoto.value,
  }),
)

const canSubmit = computed(
  () => query.value.trim().length > 0 || hasPhoto.value,
)

const showSplit = computed(
  () => view.value === 'search' && !props.compact && mode.value !== 'idle',
)

const showRecent = computed(
  () => view.value === 'search' && mode.value === 'idle' && recentItems.value.length > 0,
)

/** Figma: hide the whole block at zero matches; never together with Add detail. */
const showSuggestions = computed(
  () =>
    view.value === 'search'
    && (mode.value === 'typing' || mode.value === 'photo-typing' || mode.value === 'empty')
    && live.suggestions.value.length > 0,
)

const showAddDetail = computed(
  () => view.value === 'search' && mode.value === 'photo-detail',
)

const showPreview = computed(
  () => view.value === 'search' && mode.value !== 'idle',
)

/** Mobile sticky footer «See all» (States 2–4, not 5). */
const showCompactSeeAll = computed(
  () =>
    props.compact
    && showPreview.value
    && mode.value !== 'empty'
    && (live.total.value ?? 0) > 0,
)

/* State 5 — Or browse (real taxonomy roots) */
const categoryTreeQuery = useCategoryTreeQuery()
const browseCategories = computed(() =>
  resolveFairHomeV2SearchBrowseCategories(categoryTreeQuery.data.value),
)

const detailChips = computed(() =>
  FAIR_HOME_V2_SEARCH_ADD_DETAIL_CHIP_KEYS.map((key) => t(key)),
)

/* ------------------------------------------------------------------ */
/* Shell                                                               */
/* ------------------------------------------------------------------ */

const rootClass = computed(() => ({
  'fair-home-v2-search': true,
  'fair-home-v2-search--compact': props.compact,
  'fair-home-v2-search--desktop': !props.compact,
  'fair-home-v2-search--photo-select': view.value === 'photo-select',
  [`fair-home-v2-search--${mode.value}`]: view.value === 'search',
}))

const panelStyle = computed(() => {
  if (props.compact) return undefined
  return {
    '--fair-h2-search-drawer-w': `${showSplit.value
      ? FAIR_HOME_V2_SEARCH_DRAWER_WIDTH_RESULTS
      : FAIR_HOME_V2_SEARCH_DRAWER_WIDTH_DESKTOP}px`,
  }
})

function revokePhotoObjectUrl() {
  if (photoObjectUrl.value) {
    URL.revokeObjectURL(photoObjectUrl.value)
    photoObjectUrl.value = null
  }
}

function resetPhoto() {
  photoToken += 1
  revokePhotoObjectUrl()
  photoUrl.value = null
  photoKey.value = null
  photoTag.value = null
  photoParts.value = []
  photoSelectedId.value = FAIR_HOME_V2_SEARCH_WHOLE_OUTFIT_ID
  photoDetecting.value = false
  view.value = 'search'
}

function resetAll() {
  query.value = ''
  resetPhoto()
  live.reset()
}

function focusInput() {
  void nextTick(() => {
    inputRef.value?.focus()
    autosizeInput()
  })
}

/** Figma State 4: input grows with long text (multiline underline). */
function autosizeInput() {
  const el = inputRef.value
  if (!el) return
  el.style.height = 'auto'
  el.style.height = `${el.scrollHeight}px`
}

watch(query, () => autosizeInput())

watch(
  () => props.open,
  async (isOpen) => {
    if (typeof document !== 'undefined') {
      document.body.style.overflow = isOpen ? 'hidden' : ''
    }
    if (isOpen) {
      window.addEventListener('keydown', handleKeydown)
      await nextTick()
      if (view.value === 'search') focusInput()
    } else {
      window.removeEventListener('keydown', handleKeydown)
      resetAll()
    }
  },
)

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown)
  revokePhotoObjectUrl()
  if (typeof document !== 'undefined') {
    document.body.style.overflow = ''
  }
})

function close() {
  emit('update:open', false)
}

function handleKeydown(event: KeyboardEvent) {
  if (!props.open || event.key !== 'Escape') return
  event.preventDefault()
  if (view.value === 'photo-select') {
    handlePhotoSelectBack()
    return
  }
  close()
}

/* ------------------------------------------------------------------ */
/* Input                                                               */
/* ------------------------------------------------------------------ */

function handleClearQuery() {
  query.value = ''
  focusInput()
}

function handleInputKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    handleSubmit()
  }
}

/* ------------------------------------------------------------------ */
/* Submit → Results Page                                               */
/* ------------------------------------------------------------------ */

function handleSubmit() {
  if (!canSubmit.value) return
  // The tag ("Whole outfit" / "Jacket") names the photo region; it is a label
  // for Recent, not a query — as path text it became a text search for those
  // words. Photo-only goes as `?image=` with an empty param.
  const text = query.value.trim()
  const key = hasPhoto.value ? photoKey.value : null
  pushRecent({
    text: query.value.trim(),
    photoUrl: key ?? undefined,
    photoKey: key ?? undefined,
    photoTag: hasPhoto.value ? photoTag.value ?? undefined : undefined,
  })
  const location = buildFairHomeV2SearchLocation(text, key, props.audience)
  if (!location) return
  close()
  void router.push(location)
}

function handleBrowse(category: FairHomeV2SearchBrowseCategory) {
  close()
  void router.push(buildFairHomeV2SearchBrowseLocation(category, props.audience))
}

function handleCardClick(card: FairHomeV2SearchPreviewCard) {
  if (card.isDemo) return
  close()
  void router.push({
    name: 'product',
    params: {
      productId: String(card.id),
      productVariantId: String(card.variantId),
    },
  })
}

/* ------------------------------------------------------------------ */
/* Recent / Suggestions / Add detail                                    */
/* ------------------------------------------------------------------ */

/** Figma: tapping a row restores text/photo and moves to the Typing state. */
function handleRecentClick(item: FairHomeV2SearchRecentItem) {
  query.value = item.text || ''
  if (item.photoKey || item.photoUrl) {
    photoToken += 1
    revokePhotoObjectUrl()
    photoUrl.value = item.photoUrl ?? item.photoKey ?? null
    photoKey.value = item.photoKey ?? item.photoUrl ?? null
    photoTag.value = item.photoTag || t('fair_home_v2.search.whole_outfit')
    photoParts.value = []
    photoSelectedId.value = FAIR_HOME_V2_SEARCH_WHOLE_OUTFIT_ID
    photoDetecting.value = false
  } else {
    resetPhoto()
  }
  view.value = 'search'
  focusInput()
}

function handleSuggestionClick(label: string) {
  query.value = label
  focusInput()
}

/** Figma State 3: chip appends text (no submit) → State 4. */
function handleAddDetailChip(label: string) {
  const current = query.value.trim()
  query.value = current ? `${current} ${label}` : label
  focusInput()
}

function suggestionParts(label: string) {
  return splitFairHomeV2SearchSuggestionMatch(label, query.value)
}

/* ------------------------------------------------------------------ */
/* Photo — upload + detect (States 6–7)                                 */
/* ------------------------------------------------------------------ */

function handleAddPhotoClick() {
  fileInputRef.value?.click()
}

async function fetchImageParts(imageRef: string): Promise<FairHomeImageSearchPart[]> {
  const keys = Array.from(
    new Set([imageRef, normalizeFairHomeImageSearchKey(imageRef)].filter(Boolean)),
  )
  for (const key of keys) {
    try {
      const res = await getImageParts(key)
      const parts = parseFairHomeImageSearchParts(res.data)
      if (parts.length) return parts
    } catch {
      // try next key form
    }
  }
  return []
}

async function handleFileChange(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file) return

  resetPhoto()
  const token = ++photoToken
  const url = URL.createObjectURL(file)
  photoObjectUrl.value = url
  photoUrl.value = url
  photoDetecting.value = true
  view.value = 'photo-select'

  try {
    const [size, upload] = await Promise.all([
      loadFairHomeV2SearchImageSize(url).catch(() => ({ width: 0, height: 0 })),
      uploadImage(file),
    ])
    if (token !== photoToken) return
    const imgUrl = (upload.data as UploadImageResponse | undefined)?.img_url
    if (!imgUrl) throw new Error('Missing img_url')
    photoKey.value = imgUrl

    const rawParts = await fetchImageParts(imgUrl)
    if (token !== photoToken) return
    photoParts.value = mapFairHomeV2SearchPhotoParts(rawParts, size, (index) =>
      t(getFairHomeImageSearchPartFallbackKey(index)),
    )
  } catch {
    if (token !== photoToken) return
    // Upload/detect failed → State 7 (Whole outfit only); submit degrades to text.
    photoParts.value = []
  } finally {
    if (token === photoToken) photoDetecting.value = false
  }
}

/** Back arrow: cancel the whole photo selection → Search (State 1/2). */
function handlePhotoSelectBack() {
  resetPhoto()
  focusInput()
}

function handlePhotoSelectContinue(selectedId: string) {
  photoSelectedId.value = selectedId
  if (selectedId === FAIR_HOME_V2_SEARCH_WHOLE_OUTFIT_ID) {
    photoTag.value = t('fair_home_v2.search.whole_outfit')
  } else {
    const part = photoParts.value.find((item) => item.id === selectedId)
    photoTag.value = part?.label ?? t('fair_home_v2.search.whole_outfit')
  }
  view.value = 'search'
  focusInput()
}

/** Photo chip body → back to photo-select to edit the selection. */
function handlePhotoChipClick() {
  if (!photoUrl.value) return
  view.value = 'photo-select'
}

/** Photo chip × → State 2 if text remains, else State 1. */
function handlePhotoChipRemove() {
  resetPhoto()
  focusInput()
}
</script>

<template>
  <Teleport to="body">
    <Transition name="fair-h2-search">
      <div
        v-if="open"
        :class="rootClass"
        :style="panelStyle"
        role="dialog"
        aria-modal="true"
        :aria-label="t('fair_home_v2.search.title')"
        data-unit="H14"
        :data-figma="compact ? '50:10480' : '50:10142'"
        :data-mode="view === 'search' ? mode : view"
      >
        <button
          type="button"
          class="fair-home-v2-search__mask"
          :aria-label="t('fair_home_v2.search.close')"
          @click="close"
        />

        <aside class="fair-home-v2-search__panel">
          <!-- States 6–7: photo select -->
          <FairHomeV2SearchPhotoSelect
            v-if="view === 'photo-select' && photoUrl"
            :image-url="photoUrl"
            :parts="photoParts"
            :selected-id="photoSelectedId"
            :compact="compact"
            :detecting="photoDetecting"
            @back="handlePhotoSelectBack"
            @close="close"
            @continue="handlePhotoSelectContinue"
            @update:selected-id="photoSelectedId = $event"
          />

          <!-- States 1–5: search -->
          <template v-else>
            <header class="fair-home-v2-search__header">
              <span
                v-if="compact"
                class="fair-home-v2-search__header-spacer"
                aria-hidden="true"
              />
              <h2 class="fair-home-v2-search__title">
                {{ t('fair_home_v2.search.title') }}
              </h2>
              <FairIconButton
                icon="close"
                :size="compact ? 'mobile' : 'medium'"
                :aria-label="t('fair_home_v2.search.close')"
                @click="close"
              />
            </header>
            <div
              v-if="!compact"
              class="fair-home-v2-search__header-rule"
              aria-hidden="true"
            />

            <div class="fair-home-v2-search__body">
              <!-- Desktop: Preview results column (left) -->
              <FairHomeV2SearchPreview
                v-if="showSplit"
                :mode="mode"
                :cards="live.cards.value"
                :total="live.total.value"
                :loading="live.loading.value"
                :browse-categories="browseCategories"
                @see-all="handleSubmit"
                @browse="handleBrowse"
                @card-click="handleCardClick"
              />
              <div
                v-if="showSplit"
                class="fair-home-v2-search__divider"
                aria-hidden="true"
              />

              <div class="fair-home-v2-search__main">
                <div class="fair-home-v2-search__search-block">
                  <div class="fair-home-v2-search__input-row">
                    <label
                      class="fair-home-v2-search__field"
                      :class="{ 'fair-home-v2-search__field--filled': query.length > 0 }"
                    >
                      <FairIconContainer
                        name="search"
                        class="fair-home-v2-search__field-icon"
                      />
                      <textarea
                        ref="inputRef"
                        v-model="query"
                        class="fair-home-v2-search__input"
                        rows="1"
                        autocomplete="off"
                        enterkeyhint="search"
                        :placeholder="t('fair_home_v2.search.placeholder')"
                        :aria-label="t('fair_home_v2.search.placeholder')"
                        @keydown="handleInputKeydown"
                      />
                      <button
                        v-if="query"
                        type="button"
                        class="fair-home-v2-search__clear-query"
                        :aria-label="t('fair_home_v2.search.clear_query')"
                        @click="handleClearQuery"
                      >
                        <FairIcon
                          name="close-small"
                          :size="compact ? 20 : 16"
                        />
                      </button>
                      <span
                        class="fair-home-v2-search__field-underline"
                        aria-hidden="true"
                      />
                    </label>
                    <FairIconButton
                      icon="arrow-right"
                      :size="compact ? 'mobile' : 'small'"
                      :disabled="!canSubmit"
                      :aria-label="t('fair_home_v2.search.submit')"
                      @click="handleSubmit"
                    />
                  </div>

                  <!-- Photo chip (States 3–4) -->
                  <div
                    v-if="hasPhoto && photoUrl && photoTag"
                    class="fair-home-v2-search__photo-chip-row"
                  >
                    <button
                      type="button"
                      class="fair-home-v2-search__photo-chip"
                      :aria-label="t('fair_home_v2.search.photo_edit')"
                      @click="handlePhotoChipClick"
                    >
                      <img
                        class="fair-home-v2-search__photo-chip-thumb"
                        :src="photoUrl"
                        alt=""
                        width="36"
                        height="36"
                      >
                      <span class="fair-home-v2-search__photo-chip-label">
                        {{ photoTag }}
                      </span>
                    </button>
                    <button
                      type="button"
                      class="fair-home-v2-search__photo-chip-remove"
                      :aria-label="t('fair_home_v2.search.photo_remove')"
                      @click="handlePhotoChipRemove"
                    >
                      <FairIcon
                        name="close-small"
                        :size="compact ? 20 : 16"
                      />
                    </button>
                  </div>

                  <!-- Figma: Add photo stays visible while no photo is attached -->
                  <FairButton
                    v-if="!hasPhoto"
                    class="fair-home-v2-search__add-photo"
                    variant="tonal"
                    size="m"
                    :rounded="false"
                    leading-icon="plus"
                    :label="t('fair_home_v2.search.add_photo')"
                    @click="handleAddPhotoClick"
                  />
                  <input
                    ref="fileInputRef"
                    class="fair-home-v2-search__file"
                    type="file"
                    accept="image/*"
                    tabindex="-1"
                    @change="handleFileChange"
                  >
                </div>

                <!-- State 3: Add detail chips -->
                <div
                  v-if="showAddDetail"
                  class="fair-home-v2-search__add-detail"
                >
                  <span class="fair-home-v2-search__add-detail-label">
                    {{ t('fair_home_v2.search.add_detail') }}
                  </span>
                  <div class="fair-home-v2-search__add-detail-chips">
                    <button
                      v-for="chip in detailChips"
                      :key="chip"
                      type="button"
                      class="fair-home-v2-search__detail-chip"
                      @click="handleAddDetailChip(chip)"
                    >
                      {{ chip }}
                    </button>
                  </div>
                </div>

                <!-- State 1: Recent searches -->
                <div
                  v-if="showRecent"
                  class="fair-home-v2-search__recent"
                >
                  <div class="fair-home-v2-search__recent-head">
                    <span class="fair-home-v2-search__recent-label">
                      {{ t('fair_home_v2.search.recent') }}
                    </span>
                    <FairLink
                      type="static"
                      :label="t('fair_home_v2.search.clear_recent')"
                      @click="() => void clearRecent()"
                    />
                  </div>
                  <ul class="fair-home-v2-search__recent-list">
                    <li
                      v-for="item in recentItems"
                      :key="item.id"
                    >
                      <button
                        type="button"
                        class="fair-home-v2-search__recent-row"
                        @click="handleRecentClick(item)"
                      >
                        <span class="fair-home-v2-search__recent-leading">
                          <img
                            v-if="item.photoUrl"
                            class="fair-home-v2-search__recent-thumb"
                            :src="item.photoUrl"
                            alt=""
                            width="36"
                            height="36"
                          >
                          <FairIconContainer
                            v-else
                            name="search-history"
                            :size="16"
                          />
                        </span>
                        <span class="fair-home-v2-search__recent-text">
                          {{ recentItemLabel(item) }}
                        </span>
                      </button>
                    </li>
                  </ul>
                </div>

                <!-- States 2/4: Suggestions -->
                <div
                  v-if="showSuggestions"
                  class="fair-home-v2-search__suggestions"
                >
                  <span class="fair-home-v2-search__suggestions-label">
                    {{ t('fair_home_v2.search.suggestions') }}
                  </span>
                  <ul class="fair-home-v2-search__suggestions-list">
                    <li
                      v-for="item in live.suggestions.value"
                      :key="item.id"
                    >
                      <button
                        type="button"
                        class="fair-home-v2-search__suggestion-row"
                        @click="handleSuggestionClick(item.label)"
                      >
                        <template
                          v-for="(parts, idx) in [suggestionParts(item.label)]"
                          :key="idx"
                        >
                          <span>{{ parts.before }}</span>
                          <strong>{{ parts.match }}</strong>
                          <span>{{ parts.after }}</span>
                        </template>
                      </button>
                    </li>
                  </ul>
                </div>

                <!-- Mobile: Search results after controls (Figma 50:10483) -->
                <FairHomeV2SearchPreview
                  v-if="compact && showPreview"
                  :mode="mode"
                  :cards="live.cards.value"
                  :total="live.total.value"
                  :loading="live.loading.value"
                  :browse-categories="browseCategories"
                  compact
                  :show-see-all="false"
                  @browse="handleBrowse"
                  @card-click="handleCardClick"
                />
              </div>
            </div>

            <!-- Mobile sticky footer: See all N results (States 2–4) -->
            <footer
              v-if="showCompactSeeAll"
              class="fair-home-v2-search__footer"
            >
              <FairButton
                class="fair-home-v2-search__see-all"
                variant="primary"
                size="m"
                :rounded="false"
                trailing-icon="arrow-right"
                :label="t('fair_home_v2.search.see_all', { count: live.total.value ?? 0 })"
                @click="handleSubmit"
              />
            </footer>
          </template>
        </aside>
      </div>
    </Transition>
  </Teleport>
</template>
